import { useMemo, useRef, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Grid, IconButton } from '@mui/material'
import { GridEventListener } from '@mui/x-data-grid'
import { format, parseJSON } from 'date-fns'
import { useForm } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { ControlledTextField, FplDataGrid, IGridColumn } from 'components/common'
import { DATE_FORMAT, DEBOUNCE_DELAY } from 'constants/index'
import { CancellationOrLostReasonFilterInput } from 'generated/graphql'
import { GET_PAGED_CANCELLATION_OR_LOST_REASONS } from 'graphql/queries/BookingQueries'

import { CancellationOrLostReasonsDialog } from './CancellationOrLostReasonsDialog'

const columns: IGridColumn[] = [
  { field: 'name', headerName: 'Name', minWidth: 300, flex: 0.5 },
  { field: 'code', headerName: 'Code', minWidth: 200, flex: 0.5 },
  { field: 'createdBy', headerName: 'Created By', minWidth: 200, flex: 0.5 },
  {
    field: 'createdAt',
    headerName: 'Created At',
    minWidth: 200,
    flex: 0.5,
    valueFormatter: ({ value }) => value && format(parseJSON(value as string), DATE_FORMAT),
  },
  { field: 'isActive', headerName: 'Is Active', minWidth: 100, flex: 0.5 },
  {
    field: 'sendCustomerEmailNotification',
    headerName: 'Email notification',
    minWidth: 150,
    flex: 0.5,
    type: 'boolean',
  },
]

const CancellationOrLostReasons = () => {
  const fplDataGridActionsRef = useRef<any>()

  const [dialog, setDialog] = useState<{ open: boolean; id: string | null }>({
    id: null,
    open: false,
  })

  const { control, watch, setValue } = useForm({
    defaultValues: {
      search: '',
    },
  })

  const [searchInput] = useDebounce(watch('search', ''), DEBOUNCE_DELAY)
  const searchValue: string = searchInput.length >= 3 ? searchInput : ''

  const handleRowClick: GridEventListener<'rowClick'> = ({ id }) => {
    setDialog({ open: true, id: String(id) })
  }

  const handleAddCancellationOrLostReason = () => {
    setDialog({ open: true, id: null })
  }

  const handleCloseDialog = () => {
    setDialog({ open: false, id: null })
  }

  const handleSearchClearClick = () => {
    setValue('search', '')
  }

  const filter: CancellationOrLostReasonFilterInput = useMemo(() => {
    return {
      or: [{ name: { contains: searchValue } }],
    }
  }, [searchValue])

  return (
    <>
      <FplDataGrid
        ref={fplDataGridActionsRef}
        query={GET_PAGED_CANCELLATION_OR_LOST_REASONS}
        entityName='pagedCancellationOrLostReasons'
        columns={columns}
        filter={filter}
        defaultOrder={{ field: 'isActive', sort: 'desc' }}
        toolbar={{
          caption: 'Cancellation Or Lost Reasons',
          leftSide: (
            <Grid item>
              <Button variant='outlined' size='small' onClick={handleAddCancellationOrLostReason}>
                New Cancellation Or Lost Reason
              </Button>
            </Grid>
          ),
          rightSide: (
            <Grid item>
              <Box width='210px'>
                <ControlledTextField
                  control={control}
                  name='search'
                  label='Search'
                  defaultValue=''
                  size='small'
                  endAdornment={
                    searchInput && (
                      <IconButton size='small' onClick={handleSearchClearClick}>
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    )
                  }
                />
              </Box>
            </Grid>
          ),
        }}
        onRowClick={handleRowClick}
      />

      {dialog.open && (
        <CancellationOrLostReasonsDialog
          fplDataGridActionsRef={fplDataGridActionsRef}
          id={dialog.id}
          openDialog={dialog.open}
          onCloseDialog={handleCloseDialog}
        />
      )}
    </>
  )
}

export default CancellationOrLostReasons
